import {
  CHECK_VALIDATION_PERIOD_OPTIONS,
  CHECK_VALIDATION_QUEUE_TIME_OPTIONS,
  CHECK_VALIDATION_STATUS_FILTER_OPTIONS
} from '@/constants/check-validation.constants'

export type CheckValidationFilterTagType =
  | 'search'
  | 'status'
  | 'overdue'
  | 'period'
  | 'unit'
  | 'schedule'

// Estruturalmente compatível com o FilterTag do ActiveFilters.vue. Declarado aqui
// de propósito: importar o type nomeado de um .vue dentro de um .ts quebra o build
// webpack (o ts-loader só enxerga o export default via shim).
export interface CheckValidationFilterTag {
  id: string
  label: string
  type: CheckValidationFilterTagType
}

export interface CheckValidationFilterTagsState {
  search: string
  statuses: string[]
  onlyOverdue: boolean
  periodDays: number | null
  unitIds: string[]
  scheduleIds: string[]
  // Opções vindas dos stores, para traduzir id → nome exibido no chip.
  unitOptions: { label: string; value: string }[]
  scheduleOptions: { label: string; value: string }[]
}

const labelFor = (options: { label: string; value: string }[], value: string): string | null =>
  options.find((option) => option.value === value)?.label ?? null

// Ids qualificados (`status-APPROVED`, `unit-1`) para não colidirem entre
// categorias: o ActiveFilters usa o id no data-testid do botão de remover.
const optionTags = (
  ids: string[],
  options: { label: string; value: string }[],
  type: CheckValidationFilterTagType
): CheckValidationFilterTag[] =>
  ids
    // Opção ainda não carregada (fetch em voo) não vira chip sem rótulo — ela
    // aparece assim que a lista do store chega, porque isto é um computed.
    .map((id) => ({ id, label: labelFor(options, id) }))
    .filter((option): option is { id: string; label: string } => option.label !== null)
    .map((option) => ({ id: `${type}-${option.id}`, label: option.label, type }))

/**
 * Chips de filtro ativo da fila de avaliação de evidências. Os defaults da aba
 * ("Em análise", "Últimos 30 dias") TAMBÉM viram chip: são filtros aplicados e
 * removíveis — o que muda com eles é só o botão "Limpar filtros", que a barra
 * mostra apenas quando algo difere do default.
 *
 * Função pura para ser testada sem montar a barra (que puxa Vuetify).
 */
export const buildCheckValidationFilterTags = (
  state: CheckValidationFilterTagsState
): CheckValidationFilterTag[] => {
  const tags: CheckValidationFilterTag[] = []

  const search = state.search.trim()
  if (search) tags.push({ id: 'search', label: `Busca: ${search}`, type: 'search' })

  tags.push(...optionTags(state.statuses, CHECK_VALIDATION_STATUS_FILTER_OPTIONS, 'status'))

  if (state.onlyOverdue) {
    const overdue = CHECK_VALIDATION_QUEUE_TIME_OPTIONS.find((option) => option.value === 'overdue')
    if (overdue) tags.push({ id: 'overdue', label: overdue.label, type: 'overdue' })
  }

  if (state.periodDays !== null) {
    const period = CHECK_VALIDATION_PERIOD_OPTIONS.find(
      (option) => option.value === state.periodDays
    )
    if (period) tags.push({ id: 'period', label: period.label, type: 'period' })
  }

  tags.push(...optionTags(state.unitIds, state.unitOptions, 'unit'))
  tags.push(...optionTags(state.scheduleIds, state.scheduleOptions, 'schedule'))

  return tags
}
